import { useState, useCallback, useEffect } from 'react';
import { canEnterExtraPages, multiPageRequiresPayment } from '../lib/multiPageEligibility';
/* The price is read from the pricing config, never typed into the copy. A
   literal here would silently disagree with checkout the day the price moves,
   and the customer would read one number and be charged another. */
import { PLAN_PRICES } from '../config/pricing';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { StepIndicator } from '@/components/shared/UiElements';
import { Upload, Globe, Scan, ArrowRight, ArrowLeft, X, FileText, Image as ImageIcon, File, Check } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AuditCheckoutPopup from '@/components/AuditCheckoutPopup';
import { addOnsFrom } from '@/lib/checkoutLink';
import { track, hashDomain } from '@/lib/analytics';
import '@/styles/forms.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The file audit's name for the order (and so the recovery email): the first
// file's name, and how many more there were.
function fileLabel(files) {
  if (!files.length) return null;
  const more = files.length - 1;
  return files[0].name + (more ? ` and ${more} more file${more === 1 ? '' : 's'}` : '');
}
// Grouped rather than one flat list: at 40 entries a bare <select> becomes a
// scroll-hunt. Groups are the user's mental model of their own sector, and
// the value stored is still the plain label - the backend takes it free-form
// (server.py reads audit.industry and only special-cases "Other").
const INDUSTRY_GROUPS = [
  ['Software & Technology', [
    'SaaS', 'Developer Tools', 'AI / ML', 'Cybersecurity', 'Data & Analytics',
    'Cloud & Infrastructure', 'No-code / Automation',
  ]],
  ['Commerce', [
    'E-commerce', 'Marketplace', 'Retail', 'Fashion & Apparel',
    'Food & Beverage', 'Grocery & Delivery', 'Subscription Box',
  ]],
  ['Money', [
    'FinTech', 'Banking', 'Insurance', 'Investing & Wealth',
    'Crypto & Web3', 'Accounting & Tax', 'Payments',
  ]],
  ['Health & Wellbeing', [
    'HealthTech', 'Telemedicine', 'Fitness & Wellness', 'Mental Health',
    'Pharma & Life Sciences', 'Medical Devices',
  ]],
  ['People & Learning', [
    'EdTech', 'Online Courses', 'Recruitment & HR', 'Community & Social',
    'Social Media', 'Dating', 'Non-profit & Charity',
  ]],
  ['Work & Operations', [
    'Productivity', 'Project Management', 'CRM & Sales', 'Marketing Tools',
    'Logistics & Supply Chain', 'Manufacturing', 'Construction & Trades',
    'Legal', 'Real Estate & PropTech', 'Agency & Consulting',
  ]],
  ['Consumer & Leisure', [
    'Gaming', 'Media & Streaming', 'Travel & Hospitality', 'Events & Ticketing',
    'Sports', 'Automotive', 'Home & Interiors', 'Pets',
  ]],
  ['Public & Other', [
    'Government & Public Sector', 'Energy & Utilities', 'Agriculture',
    'Other',
  ]],
];
const goals = ['Increase conversions', 'Improve retention', 'Reduce churn', 'Improve onboarding', 'Boost engagement', 'Reduce support tickets', 'General UX review'];


const getFileIcon = (file) => file.type === 'application/pdf' ? FileText : ImageIcon;

/* WEB or MOBILE APP, asked only when the artefact cannot answer it.
 *
 * A Figma frame and an uploaded screenshot look identical either way, and the
 * answer changes the verdict on every dimension: an app has a tab bar rather
 * than a header nav, no fold, no hover state, and 44pt targets as a platform
 * rule. It also decides whether Mobile UX can be scored at all - for an app,
 * the frames ARE the mobile evidence.
 *
 * A plain website URL is not asked: the artefact already answers it.
 *
 * Deliberately NOT defaulted. "" is a real third value meaning the customer
 * did not say, and the backend assumes nothing from it. */
function PlatformChoice({ value, onChange }) {
  const options = [
    { v: 'WEB', label: 'Website', hint: 'Desktop or responsive web' },
    { v: 'MOBILE_APP', label: 'Mobile app', hint: 'iOS or Android screens' },
  ];
  return (
    <fieldset className="uxa-field" style={{ border: 0, padding: 0, margin: '1.25rem 0 0' }}>
      <legend className="uxa-label" style={{ padding: 0 }}>Is this a website or a mobile app?</legend>
      <p className="uxa-helper" style={{ marginTop: 0 }}>
        Changes what we measure against. An app is judged on tab bars, thumb reach and
        touch targets; a website on nav, fold and hover.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map(o => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(value === o.v ? '' : o.v)}
            aria-pressed={value === o.v}
            data-testid={`platform-${o.v.toLowerCase()}`}
            className="uxa-platform-opt"
            data-selected={value === o.v ? 'true' : undefined}
          >
            <span className="uxa-platform-opt-title">{o.label}</span>
            <span className="uxa-platform-opt-hint">{o.hint}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function NewAuditPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [inputType, setInputType] = useState('SCREENSHOT');
  const [files, setFiles] = useState([]);
  const [urlInput, setUrlInput] = useState('');
  const [appName, setAppName] = useState('');
  const [appDescription, setAppDescription] = useState('');
  const [industry, setIndustry] = useState('');
  // '' | 'WEB' | 'MOBILE_APP'. Asked only where the artefact cannot answer it.
  const [platform, setPlatform] = useState('');
  const [goal, setGoal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [urlError, setUrlError] = useState('');
  /* Pages 2 and 3. Two slots, always present, so the array index IS the field
     and there is no add/remove state to keep in sync with the DOM. */
  const [extraUrls, setExtraUrls] = useState(['', '']);
  const [extraErrors, setExtraErrors] = useState(['', '']);
  const [dropErrors, setDropErrors] = useState([]);
  const [userPlan, setUserPlan] = useState('FREE');
  const [auditLimit, setAuditLimit] = useState(null);
  const [showAuditCheckout, setShowAuditCheckout] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(false);
  // A recovery email's checkout (?resume=<ref>): what the server says it was.
  const [resumed, setResumed] = useState(null);

  useEffect(() => {
    // Check for payment return from PayPal
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const paymentId = params.get('paymentId');
    const payerId = params.get('PayerID');
    
    // Handle successful payment return from PayPal — redirect to thank-you handled by ThankYouPage
    if (paymentStatus === 'success' && paymentId && payerId) {
      // This case is now handled by ThankYouPage; clean URL and proceed normally
      window.history.replaceState({}, '', '/audit/new');
      // fall through to fetchUserData
    } else if (paymentStatus === 'cancelled') {
      window.history.replaceState({}, '', '/audit/new');
    }

    // Check if user already paid (stored by ThankYouPage after successful execution)
    const storedPaymentId = sessionStorage.getItem('pending_payment_id');
    if (storedPaymentId) {
      setPaymentSessionId(storedPaymentId);
      setPaymentRequired(false); // Payment already completed — clear the gate
    }

    // A link from a report's free re-audit banner carries the page URL. The
    // limit check needs it: the free re-audit is granted per page, and without
    // the URL the check would show the paywall instead.
    // The page also survives the trip to PayPal and back: the checkout keeps
    // it in this tab's sessionStorage, as it already keeps the payment id.
    const prefillUrl = params.get('url') || sessionStorage.getItem('pending_audit_url') || '';

    // A recovery email's "Finish your audit": the saved order, restored by
    // the server for its owner, opens on the order summary. If it cannot be
    // restored (paid since, plan gone, not theirs), /pricing, never a blank page.
    const resumeRef = params.get('resume');
    if (resumeRef) {
      axios.get(`${API}/payments/resume/${encodeURIComponent(resumeRef)}`, { withCredentials: true })
        .then((resp) => {
          const r = resp.data;
          if (r.audited_url) {
            setInputType('URL');
            setUrlInput(r.audited_url);
          }
          setResumed({ ...r, add_ons: addOnsFrom(r.add_ons) });
          setShowAuditCheckout(true);
        })
        .catch(() => navigate('/pricing', { replace: true }));
    }
    if (prefillUrl) {
      setInputType('URL');
      setUrlInput(prefillUrl);
    }

    const fetchUserData = async () => {
      try {
        const limitQuery = prefillUrl ? `?url=${encodeURIComponent(prefillUrl)}` : '';
        const [userResp, limitResp] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/audits/check-limit${limitQuery}`, { withCredentials: true })
        ]);
        setUserPlan(userResp.data.plan || 'FREE');
        setAuditLimit(limitResp.data);

        // If backend detected a retry-eligible payment (failed audit), use it automatically
        if (limitResp.data.payment_session_id) {
          setPaymentSessionId(limitResp.data.payment_session_id);
          setPaymentRequired(false); // Valid payment found — clear the gate
        }

        // If user can't create audit, mark payment required and show checkout popup
        if (!limitResp.data.can_create && !storedPaymentId && !paymentId) {
          setPaymentRequired(true);
          setShowAuditCheckout(true);
        }
      } catch (e) {
        console.error('Failed to fetch user data:', e);
      }
    };
    fetchUserData();
  }, []);

  // Keep the page across the trip to PayPal (see the mount effect above).
  useEffect(() => {
    if (showAuditCheckout && inputType === 'URL' && urlInput) sessionStorage.setItem('pending_audit_url', urlInput);
  }, [showAuditCheckout, inputType, urlInput]);

  const MAX_PDF_MB = 50;
  const MAX_IMG_MB = 20;

  const onDrop = useCallback((acceptedFiles) => {
    setDropErrors([]);
    const newFiles = acceptedFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      name: file.name,
      type: file.type,
      size: file.size,
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const onDropRejected = useCallback((rejectedFiles) => {
    const errors = rejectedFiles.map(({ file, errors: errs }) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const tooBig = errs.some(e => e.code === 'file-too-large');
      const wrongType = errs.some(e => e.code === 'file-invalid-type');
      const tooMany = errs.some(e => e.code === 'too-many-files');
      if (tooBig) {
        const limitMB = isPdf ? MAX_PDF_MB : MAX_IMG_MB;
        const fileMB = (file.size / (1024 * 1024)).toFixed(1);
        return `"${file.name}" is ${fileMB} MB — max allowed for ${isPdf ? 'PDF' : 'image'} files is ${limitMB} MB.`;
      }
      if (wrongType) return `"${file.name}" is not a supported file type. Use PNG, JPG, WebP, or PDF.`;
      if (tooMany) return `Too many files. Maximum 10 files per upload.`;
      return `"${file.name}" could not be added.`;
    });
    setDropErrors(errors);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'], 'application/pdf': ['.pdf'] },
    maxSize: MAX_PDF_MB * 1024 * 1024,
    maxFiles: 10,
  });

  const removeFile = (index) => {
    setDropErrors([]);
    setFiles(prev => {
      const removed = prev[index];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /* ── SAME SITE, CHECKED HERE AND AGAIN ON THE SERVER ────────────────────
     This mirrors backend/audit/page_set.py. It exists for the MESSAGE, not for
     the enforcement: the server re-validates every submission and is the only
     thing that actually decides. Doing it here as well means a customer learns
     about a wrong domain while their cursor is still in the field, rather than
     after a round trip.

     The multi-label suffix list is the same one and for the same reason: a
     last-two-labels split calls alpha.co.uk and beta.co.uk the same site. Kept
     deliberately short — the honest limit, recorded in the Python, is that an
     unlisted suffix goes permissive rather than restrictive, and this is a
     product rule rather than a security boundary. */
  const MULTI_LABEL_SUFFIXES = new Set([
    'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk', 'nhs.uk',
    'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
    'co.nz', 'net.nz', 'org.nz', 'co.jp', 'or.jp', 'ne.jp', 'co.kr',
    'com.cn', 'net.cn', 'org.cn', 'co.in', 'net.in', 'org.in',
    'com.sg', 'com.hk', 'com.br', 'net.br', 'org.br', 'com.mx',
    'com.ar', 'co.za', 'com.tr', 'com.pl', 'com.es', 'com.pt', 'co.il',
  ]);

  const registrableDomain = (url) => {
    let host;
    try { host = new URL(url.trim()).hostname.toLowerCase(); } catch { return ''; }
    if (!host) return '';
    if (host.startsWith('www.')) host = host.slice(4);
    const labels = host.split('.');
    if (labels.length < 2) return host;
    if (labels.every((l) => /^\d+$/.test(l))) return host;   // an IP has no domain
    const lastTwo = labels.slice(-2).join('.');
    if (MULTI_LABEL_SUFFIXES.has(lastTwo) && labels.length >= 3) return labels.slice(-3).join('.');
    return lastTwo;
  };

  const isFigmaUrl = (url) => {
    try { return new URL(url).hostname.includes('figma.com'); } catch { return false; }
  };

  /* The block that used to live here said "Figma URLs cannot be audited
   * directly". It was stale: the collector had _is_figma_url and a dedicated
   * 45s navigation budget the whole time. It was opened behind ?figma=1 on
   * 2026-08-21, the probe run produced a real report, and the flag is gone -
   * as its own comment committed it would be, either way.
   *
   * A Figma link now goes down the same path as any other URL. The collector
   * renders the file's frames through Figma's REST API rather than
   * screenshotting the Figma app, so what gets audited is the design. */

  const handleUrlChange = (e) => {
    const val = e.target.value;
    setUrlInput(val);
    setUrlError('');
    /* Page 1 is the anchor, so changing it re-judges the others. Leaving stale
       errors up would tell someone their page 2 is wrong about a domain they
       have just stopped using. */
    setExtraErrors(extraUrls.map((u) => sameSiteError(u, val)));
  };

  /* Empty is not an error — pages 2 and 3 are optional. */
  const sameSiteError = (candidate, anchorUrl) => {
    const value = (candidate || '').trim();
    if (!value) return '';
    const anchor = registrableDomain(anchorUrl);
    if (!anchor) return '';          // nothing to compare against yet
    const domain = registrableDomain(value);
    if (!domain) return 'That is not a URL we can read. Include the full address, starting with https://';
    if (domain !== anchor) {
      /* Both sides named. "Pages must share a domain" leaves the reader working
         out which of their URLs is the odd one. */
      return `This page is on ${domain}. Every page must be on ${anchor} — the site set by page 1.`;
    }
    return '';
  };

  const handleExtraUrlChange = (i, value) => {
    const next = [...extraUrls];
    next[i] = value;
    setExtraUrls(next);
    const errs = [...extraErrors];
    errs[i] = sameSiteError(value, urlInput);
    setExtraErrors(errs);
  };

  /* PRO/MAX are entitled at load; a pay-per-audit customer becomes entitled the
     moment a payment session exists. Anyone else gets one page — the fields are
     rendered and disabled rather than hidden, so the limit is visible instead
     of being a thing they discover. */
  /* The kill switch, flipped with the renderer. The backend refuses a
     multi-page submission independently (audit/page_set.py), so this is the
     visible half of a gate that does not depend on the client being honest. */
  const MULTI_PAGE_UI_ENABLED = true;

  /* WHO MAY ENTER PAGES 2 AND 3 LIVES IN src/lib/multiPageEligibility.js.
     It was inline here and shipped unreachable for the customers it was sold
     to: it required a resolved payment, and payment happens at step 4 while
     these fields are at step 2. Extracted so every case is an assertion rather
     than a source grep — see src/__tests__/multi-page-eligibility.test.js. */
  const AUDIT_PRICE_USD = PLAN_PRICES.STARTER.usd;
  const canMultiPage = canEnterExtraPages({ featureEnabled: MULTI_PAGE_UI_ENABLED });

  /* How many extra pages the customer has actually asked for, and whether that
     turns this audit into a purchase. A free allowance buys a ONE-PAGE audit;
     a second or third page is a purchase whether or not the free audit has been
     spent. Recomputed on every keystroke, so the price line appears the moment
     a second URL is typed rather than at Confirm, where it would be a surprise. */
  const extraPageCount = extraUrls.filter((u) => u && u.trim()).length;
  const paysForExtraPages = multiPageRequiresPayment({
    userPlan, paymentSessionId, extraPageCount,
  });
  /* The two roads to checkout: the free allowance is spent, OR the customer
     chose more than one page. Either way the Next button routes to payment
     instead of advancing. */
  const needsCheckout = (paymentRequired && !paymentSessionId) || paysForExtraPages;

  const canProceed = () => {
    if (step === 0) return true;
    if (step === 1) {
      if (inputType === 'SCREENSHOT') return files.length > 0;
      return urlInput.trim().length > 0 && !urlError && !extraErrors.some(Boolean);
    }
    if (step === 2) return appName.trim().length > 0;
    return true;
  };

  const handleSubmit = async () => {
    // Guard: either road to checkout — the free allowance is spent, or the
    // customer chose more than one page. The same condition the Next button
    // uses, because a submit that skipped it would reach the server and be
    // refused there, losing everything they typed.
    if (needsCheckout) {
      setShowAuditCheckout(true);
      return;
    }
    setSubmitting(true);
    setUploadProgress('Uploading files...');
    try {
      let screenshotData = [];
      if (inputType === 'SCREENSHOT' && files.length > 0) {
        setUploadProgress('Processing files... (large PDFs may take a moment)');
        const formData = new FormData();
        files.forEach(f => formData.append('files', f.file));
        let uploadResp;
        try {
          uploadResp = await axios.post(`${API}/audits/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (uploadErr) {
          console.error('File upload failed', uploadErr);
          const uploadMsg = uploadErr.response?.data?.detail || 'File upload failed. Please check your file and try again.';
          alert(uploadMsg);
          return;
        }
        screenshotData = uploadResp.data.images.map(img => img.base64);
        setUploadProgress(`Processed ${uploadResp.data.count} image(s). Creating audit...`);
      }

      // Include payment session if available (for Starter plan)
      const auditPayload = {
        input_type: inputType,
        input_data: inputType === 'URL' ? urlInput : '',
        /* Only what the customer actually filled in, and only when they are
           entitled. The server validates the set again regardless — this is
           convenience, not the rule. */
        extra_urls: inputType === 'URL' && canMultiPage
          ? extraUrls.map((u) => u.trim()).filter(Boolean)
          : [],
        app_name: appName,
        app_description: appDescription,
        industry,
        platform,
        goal,
        screenshots: screenshotData,
        payment_session_id: paymentSessionId || undefined
      };

      const auditResp = await axios.post(`${API}/audits`, auditPayload, { withCredentials: true });
      const auditId = auditResp.data.audit_id;
      track('audit_started', {
        audit_id: auditId,
        input_type: inputType,
        url_domain: inputType === 'URL' ? hashDomain(urlInput) : '',
        tier: userPlan,
        is_first_audit: (auditLimit?.audits_used ?? null) === 0,
      });
      setUploadProgress('Starting AI analysis...');
      await axios.post(`${API}/audits/${auditId}/analyze`, {}, { withCredentials: true });
      sessionStorage.removeItem('pending_payment_id');
      sessionStorage.removeItem('pending_audit_url');
      navigate(`/audit/${auditId}/report`);
    } catch (e) {
      console.error('Failed to create audit', e);
      // Check if it's a limit/payment error
      if (e.response?.status === 403 && e.response?.data?.detail) {
        setAuditLimit(e.response.data.detail);
        setPaymentRequired(true);
        setShowAuditCheckout(true);
      } else {
        alert(e.response?.data?.detail || 'Failed to create audit. Please try again.');
      }
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };


  const stepLabels = ['Type', 'Upload', 'Context', 'Confirm'];

  return (
    <div className="min-h-screen pt-20">
      <AuditCheckoutPopup
        visible={showAuditCheckout}
        onClose={() => setShowAuditCheckout(false)}
        user={user}
        setUser={setUser}
        source={resumed ? 'checkout_recovery_email' : 'quota_modal'}
        auditedUrl={inputType === 'URL' ? urlInput : null}
        auditedFile={inputType === 'SCREENSHOT' ? fileLabel(files) : null}
        initialAddOns={resumed ? resumed.add_ons : null}
        initialPromo={resumed?.promo_code}
        initialStep={resumed ? 'checkout' : 'addons'}
        reuploadFile={resumed?.audited_file}
      />
      <div className="mx-auto px-6 py-14" style={{ maxWidth: '760px' }}>
        {/* NO `initial={{ opacity: 0 }}` ON THIS FORM. THE SLIDE STAYS.
          *
          * Measured on production 2026-08-27: this wrapper and the step-0
          * panel were both stranded at opacity 0.235305 — nested, so the form
          * rendered at about 5% and could not be filled in. Two independent
          * animations on different properties (y here, x there) landing on the
          * same opacity to six decimals is ONE frame loop stopping, not two
          * animations misbehaving.
          *
          * The trigger was never identified. Four scenarios through the exact
          * production path — normal motion, close-popup-then-reload,
          * prefers-reduced-motion, and both — all came back clean, and the
          * code was unchanged between the measurement and the fix. The rAF
          * one-liner for whoever sees it next is in docs/DECISIONS.md.
          *
          * The fix does not depend on knowing. An entry animation that can
          * strand a form at 5% opacity is a bad trade on a page whose only job
          * is accepting input: opacity starts at 1, so a halted loop leaves a
          * READABLE, USABLE form that merely did not slide. Transform is safe
          * to strand; opacity is not.
          *
          * NEITHER ELEMENT IS QUOTA-GATED — this wrapper always renders and
          * `step` starts at 0 — so when it stranded it stranded for everyone,
          * first audit or not. The exhausted-quota state made it visible; it
          * did not cause it.
          *
          * Precedent: index.css:116 carries `opacity: 1 !important` commented
          * "Fix hero animation stuck issue". Same bug, blunter fix, narrower
          * scope. Guarded by no-stranded-input-surface.test.js. */}
        <motion.div initial={{ y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="uxa-form-card">
            <div className="uxa-form-card-spectrum" aria-hidden="true" />
            <div className="uxa-form-card-body">

              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1
                    style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--ink-1)' }}
                    data-testid="audit-wizard-title"
                  >
                    New UX Audit
                  </h1>
                  <p style={{ fontSize: '14px', color: 'var(--ink-2)', marginTop: '6px' }}>
                    You get one free audit every month. Results in under 5 minutes.
                  </p>
                </div>
                <span
                  className="uxa-meta"
                  style={{
                    display: 'flex', alignItems: 'center', minHeight: 'var(--target-min)', padding: '0 18px',
                    borderRadius: 'var(--radius-pill)', background: 'var(--surface-panel)', border: '1px solid var(--border-2)',
                  }}
                >
                  step {step + 1} of {stepLabels.length}
                </span>
              </div>

              <div style={{ marginTop: '28px' }}>
                <StepIndicator currentStep={step} totalSteps={stepLabels.length} labels={stepLabels} />
              </div>

              <AnimatePresence mode="wait">
                {/* Step 0: Input Type */}
                {step === 0 && (
                  <motion.div key="s0" initial={{ x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                    <p className="uxa-helper">How would you like to share your app?</p>
                    {[
                      { type: 'SCREENSHOT', icon: Upload, title: 'Upload Files', desc: 'PNG, JPG, PDF — screenshots or design files', recommended: true },
                      /* "Up to 3" is SOURCED, not a round number: extraUrls is
                         seeded ['', ''] at :126 and rendered by [0, 1].map at
                         :707, so the form offers exactly two extra fields and
                         the backend refuses more independently in
                         audit/page_set.py. It also matches the explainer at
                         :705, which this line contradicted.

                         WHY IT WAS WRONG FOR SO LONG: the sentence carried two
                         claims. "no full-site crawl" was the load-bearing half
                         — it is the thing people assume we do — and it stayed
                         true, so the sentence kept reading as correct while
                         "one public page" quietly went stale under it when
                         multi-page shipped. A true half keeps a false half
                         alive. When one claim in a compound sentence changes,
                         the whole sentence is stale, not the clause. */
                      { type: 'URL', icon: Globe, title: 'Enter URL or Figma link', desc: 'Up to 3 public pages of the same site, or a Figma design file — no logged-in pages, no full-site crawl', recommended: false },
                    ].map(opt => (
                      <label
                        key={opt.type}
                        className={`uxa-choice-card block cursor-pointer${inputType === opt.type ? ' uxa-choice-card--selected' : ''}`}
                        data-testid={`input-type-${opt.type.toLowerCase()}`}
                      >
                        <span className="uxa-choice" style={{ width: '100%' }}>
                          <input
                            type="radio"
                            name="audit-input-type"
                            value={opt.type}
                            checked={inputType === opt.type}
                            onChange={() => setInputType(opt.type)}
                          />
                          <span className="flex items-center gap-3 flex-wrap flex-1">
                            <opt.icon className="w-5 h-5 flex-none" style={{ color: 'var(--ink-1)' }} aria-hidden="true" />
                            <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink-1)' }}>{opt.title}</span>
                            {opt.recommended && <span className="uxa-tag">recommended</span>}
                          </span>
                        </span>
                        <p className="uxa-helper" style={{ paddingLeft: '34px', marginTop: '6px' }}>{opt.desc}</p>
                      </label>
                    ))}
                  </motion.div>
                )}

                {/* Step 1: Upload / URL */}
                {step === 1 && (
                  <motion.div key="s1" initial={{ x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    {inputType === 'SCREENSHOT' ? (
                      <div className="space-y-4">
                        <p className="uxa-helper">Upload screenshots or design files (PNG, JPG, PDF — max 10 files)</p>
                        <div
                          {...getRootProps()}
                          data-testid="screenshot-dropzone"
                          className={`uxa-dropzone${isDragActive ? ' uxa-dropzone--active' : ''}${dropErrors.length > 0 ? ' uxa-dropzone--error' : ''}`}
                        >
                          <input {...getInputProps()} />
                          <div className="uxa-dropzone-mark" aria-hidden="true"><Upload className="w-5 h-5" /></div>
                          <div className="uxa-dropzone-title">Drop files here</div>
                          <div className="uxa-dropzone-sub">or <b>browse your files</b></div>
                          <div className="uxa-dropzone-limits">
                            PNG, JPG, WebP · up to {MAX_IMG_MB} MB each{' '}<br />
                            PDF · up to {MAX_PDF_MB} MB, 5 pages extracted per PDF
                          </div>
                        </div>
                        {dropErrors.length > 0 && (
                          <div className="space-y-2" role="alert">
                            {dropErrors.map((err, i) => (
                              <p key={i} className="uxa-msg uxa-msg--error">
                                <span className="uxa-msg-mark" aria-hidden="true">!</span>
                                <span><strong>Error:</strong> {err}</span>
                              </p>
                            ))}
                          </div>
                        )}
                        {files.length > 0 && (
                          <>
                            <div className="uxa-filelist">
                              {files.map((f, i) => {
                                const Icon = getFileIcon(f.file);
                                return (
                                  <div key={i} className="uxa-file" data-testid={`file-item-${i}`}>
                                    {f.preview
                                      ? <img src={f.preview} alt="" className="uxa-file-thumb" />
                                      : <span className="uxa-file-thumb" aria-hidden="true"><Icon className="w-5 h-5" style={{ color: 'var(--ink-2)' }} /></span>}
                                    <span className="uxa-file-body">
                                      <span className="uxa-file-name">{f.name}</span>
                                      <span className="uxa-meta">
                                        {formatFileSize(f.size)}{f.type === 'application/pdf' ? ' · PDF, pages extracted' : ''}
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(i)}
                                      className="uxa-file-remove"
                                      aria-label={`Remove ${f.name}`}
                                      data-testid={`remove-file-${i}`}
                                    >
                                      <X className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="uxa-helper">{files.length} of 10 files · remove with the ✕.</p>
                            <PlatformChoice value={platform} onChange={setPlatform} />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="uxa-field">
                        <label className="uxa-label" htmlFor="audit-url">Product URL or Figma link</label>
                        <input
                          id="audit-url"
                          type="url"
                          value={urlInput}
                          onChange={handleUrlChange}
                          placeholder="https://yourapp.com or https://figma.com/design/…"
                          data-testid="url-input"
                          className="uxa-input"
                          aria-invalid={urlError ? 'true' : undefined}
                          aria-describedby={urlError ? 'audit-url-error' : 'audit-url-help'}
                        />
                        {urlError ? (
                          <p className="uxa-msg uxa-msg--error" id="audit-url-error" role="alert">
                            <span className="uxa-msg-mark" aria-hidden="true">!</span>
                            <span><strong>Error:</strong> {urlError}</span>
                          </p>
                        ) : (
                          <ul id="audit-url-help" className="uxa-helper space-y-2" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                            {isFigmaUrl(urlInput.trim()) ? (
                              /* A design file's caveats are not a website's. Sharing
                                 replaces "public page", and there is no live behaviour
                                 to audit at all, so saying "works best on static
                                 sites" here would be nonsense. */
                              <>
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>Set sharing to "Anyone with the link".</strong> We read the file through Figma's API, so a private file can't be reached. View access is enough — we never edit.</li>
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>We audit the design, not Figma.</strong> Your frames are rendered at full size. Figma's own toolbar, comments and canvas are not part of the audit.</li>
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>Static design only.</strong> A file has no hover, focus, scroll or loading states, so those aren't assessed. For live behaviour, submit the built page's URL instead.</li>
                              </>
                            ) : (
                              <>
                                {/* REPLACED 2026-08-24. The previous bullet promised one page per
                                    audit and told the reader to send other pages one at a time.
                                    True until multi-page, false after it, and a stale claim about
                                    what the product does is the class of thing this repo keeps
                                    having to remove. It now states the rule actually enforced.
                                    The old wording is deliberately not quoted here: a guard greps
                                    this file for it, and a comment containing the banned phrase
                                    is a hole with an explanation next to it. */}
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>The pages you enter, nothing else.</strong> We audit the exact URLs you give us, not the whole website, and every page must be on the same site.</li>
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>Public pages only.</strong> We can't sign in, so anything behind a login (dashboards, account pages, checkouts) can't be reached. For those, use Upload Files.</li>
                                <li><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>Works best on simple/static sites.</strong> JS-heavy or Cloudflare-protected pages may not render — use Upload Files for guaranteed results.</li>
                              </>
                            )}
                          </ul>
                        )}
                        {isFigmaUrl(urlInput.trim()) && (
                          <PlatformChoice value={platform} onChange={setPlatform} />
                        )}

                        {/* ── PAGES 2 AND 3 ──────────────────────────────────
                            THREE FIELDS, ALWAYS RENDERED, disabled when the
                            plan does not include them. Visible-but-disabled is
                            the honest version: it states the limit instead of
                            hiding it, and it does the upgrade work without
                            pretending everyone has the feature.

                            THE SENTENCE CARRIES THE MEANING, NOT THE DISABLED
                            STATE. A greyed-out input with placeholder text
                            reads as broken to plenty of people, and a control
                            whose only explanation is its own styling explains
                            nothing to a screen reader either. So the line above
                            the group says it in words, the inputs are
                            aria-disabled with a described-by pointing at that
                            line, and the styling is the third signal rather
                            than the only one.

                            Not shown for a Figma link: a design file is one
                            artefact, and "page 2" of it is not a thing. */}
                        {!isFigmaUrl(urlInput.trim()) && (
                          <div className="uxa-field" style={{ marginTop: 20 }}>
                            <p
                              id="multipage-explainer"
                              className="uxa-helper"
                              style={{
                                color: paysForExtraPages ? 'var(--ink-1)' : 'var(--ink-2)',
                                fontWeight: paysForExtraPages ? 700 : 400,
                                marginBottom: 10,
                              }}
                              data-testid="multipage-explainer"
                            >
                              {/* THE PRICE IS STATED BEFORE THEY TYPE, NOT AT CONFIRM.
                                  A free allowance buys a one-page audit; a second or
                                  third page is a purchase. Saying so here is the whole
                                  point of choose-then-run — the customer decides how
                                  many pages knowing what it costs, instead of meeting
                                  the charge after the work of filling the form. */}
                              {paysForExtraPages
                                ? `${extraPageCount === 1 ? 'A second page' : 'Two more pages'} makes this a $${AUDIT_PRICE_USD} audit, charged once at the end. All pages are audited together, with a cross-page pass over them.`
                                : 'Add up to two more pages of the same site — they are audited together, with a cross-page pass over all of them. Your free audit covers one page; adding a second makes it a $' + AUDIT_PRICE_USD + ' audit.'}
                            </p>
                            {[0, 1].map((i) => (
                              <div key={i} style={{ marginBottom: 10 }}>
                                <label className="uxa-label" htmlFor={`audit-url-${i + 2}`}>
                                  Page {i + 2} <span className="uxa-helper">(optional)</span>
                                </label>
                                <input
                                  id={`audit-url-${i + 2}`}
                                  type="url"
                                  value={extraUrls[i]}
                                  onChange={(e) => handleExtraUrlChange(i, e.target.value)}
                                  disabled={!canMultiPage}
                                  placeholder="https://yourapp.com/pricing" 
                                  data-testid={`url-input-${i + 2}`}
                                  className="uxa-input"
                                  aria-disabled={!canMultiPage ? 'true' : undefined}
                                  aria-invalid={extraErrors[i] ? 'true' : undefined}
                                  aria-describedby={
                                    extraErrors[i] ? `audit-url-${i + 2}-error` : 'multipage-explainer'
                                  }
                                />
                                {extraErrors[i] && (
                                  <p
                                    className="uxa-msg uxa-msg--error"
                                    id={`audit-url-${i + 2}-error`}
                                    role="alert"
                                    data-testid={`url-error-${i + 2}`}
                                  >
                                    <span className="uxa-msg-mark" aria-hidden="true">!</span>
                                    <span><strong>Error:</strong> {extraErrors[i]}</span>
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Step 2: Context */}
                {step === 2 && (
                  <motion.div key="s2" initial={{ x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                    <p className="uxa-helper">Help us understand your app for a more accurate audit</p>
                    <div className="uxa-field">
                      <label className="uxa-label" htmlFor="app-name">App Name <span aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
                      <input
                        id="app-name"
                        type="text"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        placeholder="My Amazing App"
                        data-testid="app-name-input"
                        className="uxa-input"
                        required
                      />
                      <span className="uxa-helper">Shown at the top of your report.</span>
                    </div>
                    <div className="uxa-field">
                      <label className="uxa-label" htmlFor="app-description">App Description</label>
                      <textarea
                        id="app-description"
                        value={appDescription}
                        onChange={(e) => setAppDescription(e.target.value)}
                        placeholder="Describe what your app does, who it's for, and what specific UX areas you want us to focus on..."
                        data-testid="app-description-input"
                        rows={4}
                        className="uxa-textarea"
                      />
                      <span className="uxa-helper">Optional but recommended</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="uxa-field">
                        <label className="uxa-label" htmlFor="industry">Industry</label>
                        <span className="uxa-select-wrap">
                          <select
                            id="industry"
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            data-testid="industry-select"
                            className="uxa-select"
                            style={{ color: industry ? 'var(--ink-1)' : 'var(--ink-2)' }}
                          >
                            <option value="">Select industry</option>
                            {INDUSTRY_GROUPS.map(([group, items]) => (
                              <optgroup key={group} label={group}>
                                {items.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </span>
                      </div>
                      <div className="uxa-field">
                        <label className="uxa-label" htmlFor="goal">Primary Goal</label>
                        <span className="uxa-select-wrap">
                          <select
                            id="goal"
                            value={goal}
                            onChange={(e) => setGoal(e.target.value)}
                            data-testid="goal-select"
                            className="uxa-select"
                            style={{ color: goal ? 'var(--ink-1)' : 'var(--ink-2)' }}
                          >
                            <option value="">Select goal</option>
                            {goals.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Confirm */}
                {step === 3 && (
                  <motion.div key="s3" initial={{ x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <p className="uxa-helper" style={{ marginBottom: '20px' }}>Review your audit details</p>
                    {/* --radius-lg, HELD in Phase D's classification. This is an
                        inset summary panel inside the form card, not a card among
                        peers, so it takes the size token rather than --radius-card. */}
                    <div
                      className="p-6 space-y-4"
                      style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-lg)' }}
                    >
                      {[
                        ['Input Type', inputType === 'SCREENSHOT' ? 'File Upload' : 'URL'],
                        ...(inputType === 'SCREENSHOT' ? [['Files', `${files.length} file(s) — ${files.map(f => f.type === 'application/pdf' ? 'PDF' : 'Image').join(', ')}`]] : [['URL', urlInput]]),
                        ['App Name', appName],
                        ['Industry', industry || 'Not specified'],
                        ['Goal', goal || 'Not specified'],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between gap-4 text-sm">
                          <span style={{ color: 'var(--ink-2)' }}>{label}</span>
                          <span className="font-bold truncate max-w-[250px]" style={{ color: 'var(--ink-1)' }}>{val}</span>
                        </div>
                      ))}
                      {appDescription && (
                        <div className="pt-4" style={{ borderTop: '1px solid var(--border-2)' }}>
                          <span className="text-sm block mb-1" style={{ color: 'var(--ink-2)' }}>Description</span>
                          <span className="uxa-dense" style={{ color: 'var(--ink-1)' }}>{appDescription}</span>
                        </div>
                      )}
                      {/* THE ENTITLEMENT, NOT THE FLAG, and no crown.
                          This row drew a Crown whenever userPlan !== 'FREE'
                          above planLabels[userPlan], so a past customer saw
                          "Starter Plan" under a crown on the REVIEW STEP --
                          the screen where someone decides whether to pay --
                          while holding exactly the free entitlement. `plan`
                          records "has ever paid"; one completed payment writes
                          STARTER permanently, nothing resets it, and from
                          2026-09-01 STARTER carries the same monthly free audit
                          as FREE.

                          Same string as the Account page, for the same reason:
                          the entitlement is identical on both plans, so a
                          surface that renders them differently is inventing a
                          distinction. */}
                      <div className="flex justify-between gap-4 text-sm pt-4" style={{ borderTop: '1px solid var(--border-2)' }}>
                        <span style={{ color: 'var(--ink-2)' }}>Audits</span>
                        <span className="font-bold" style={{ color: 'var(--ink-1)' }} data-testid="review-entitlement">
                          {`1 free audit per month, then $${PLAN_PRICES.STARTER.usd} each`}
                        </span>
                      </div>
                    </div>
                    {files.length > 0 && (
                      <div className="mt-6">
                        <p className="uxa-label" style={{ marginBottom: '12px' }}>Files to analyze</p>
                        <div className="flex gap-2 flex-wrap">
                          {files.map((f, i) => (
                            <span
                              key={i}
                              className="uxa-meta"
                              style={{ padding: '6px 12px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-sand-deep)', border: '1px solid var(--border-2)' }}
                            >
                              {f.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation */}
              <div
                className="flex flex-wrap justify-between items-center gap-4"
                style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--border-2)' }}
              >
                <button
                  type="button"
                  onClick={() => setStep(s => Math.max(0, s - 1))}
                  disabled={step === 0 || submitting}
                  data-testid="wizard-back"
                  className="uxa-button-secondary disabled:opacity-40"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                </button>
                {step < stepLabels.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (needsCheckout) {
                        setShowAuditCheckout(true);
                        return;
                      }
                      setStep(s => s + 1);
                    }}
                    disabled={!canProceed()}
                    data-testid="wizard-next"
                    className="uxa-submit uxa-submit--auto"
                  >
                    Next
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    aria-busy={submitting ? 'true' : undefined}
                    data-testid="wizard-submit"
                    className="uxa-submit uxa-submit--auto"
                  >
                    {submitting ? (uploadProgress || 'Processing…') : 'Start Audit'}
                    <span className="uxa-submit-knob" aria-hidden="true">
                      {submitting ? <span className="uxa-spinner" /> : <Scan className="w-4 h-4" />}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
